/**
 * Коды ошибок — зеркало `backend/src/exceptions.py`.
 *
 * Различать ошибки надо по `code`, а НЕ по HTTP-статусу: `INSUFFICIENT_FUNDS`
 * приходит с 400, ровно как обычный `REQUEST_ERROR`, и по статусу их не
 * развести.
 */
export const ERROR_CODES = [
  "REQUEST_ERROR",
  "INSUFFICIENT_FUNDS",
  "VALIDATION_ERROR",
  "AUTH_ERROR",
  "FORBIDDEN",
  "NOT_FOUND",
  "CONFLICT",
  "TOO_MANY_REQUESTS",
  "EXTERNAL_SERVICE_ERROR",
  "CONFIGURATION_ERROR",
  "NOT_IMPLEMENTED",
  "UNKNOWN_ERROR",
] as const;

export type ErrorCode = (typeof ERROR_CODES)[number];

export interface ErrorDetails {
  [key: string]: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  /**
   * Неизвестный код не должен ронять разбор: бэкенд может добавить новый.
   * `string & {}` вместо голого `string` — чтобы автодополнение продолжало
   * подсказывать известные коды, а не схлопывалось до «любая строка».
   */
  readonly code: ErrorCode | (string & {});
  /** `details` в конверте есть всегда. Исключение — 422, он собирается мимо `to_dict()`. */
  readonly details: ErrorDetails;
  /** Из `details.trace_id`. Отсутствует у 422. */
  readonly traceId: string | undefined;
  /** Заголовок `X-Request-ID`. По нему жалоба сопоставляется с логом бэкенда. */
  readonly requestId: string | undefined;

  constructor(init: {
    status: number;
    code: string;
    message: string;
    details?: ErrorDetails;
    requestId?: string | undefined;
  }) {
    super(init.message);
    this.name = "ApiError";
    this.status = init.status;
    this.code = init.code;
    this.details = init.details ?? {};
    this.traceId = typeof this.details["trace_id"] === "string" ? this.details["trace_id"] : undefined;
    this.requestId = init.requestId;
  }

  /** Строка для лога: без неё разбор инцидента упирается в «у меня не работает». */
  get logLine(): string {
    return `${this.code} ${String(this.status)} trace=${this.traceId ?? "-"} request=${this.requestId ?? "-"}`;
  }
}

/** Сеть недоступна. Отличается от ApiError: реакция на офлайн другая. */
export class NetworkError extends Error {
  constructor(cause?: unknown) {
    super("Сеть недоступна");
    this.name = "NetworkError";
    this.cause = cause;
  }
}

/** Строковое поле из `details` — суммы и лимиты приходят строками. */
export function detailString(details: ErrorDetails, key: string): string | undefined {
  const value = details[key];
  return typeof value === "string" ? value : undefined;
}

export function detailNumber(details: ErrorDetails, key: string): number | undefined {
  const value = details[key];
  return typeof value === "number" ? value : undefined;
}
